import React, { FC, useCallback, useState, useEffect, useMemo } from 'react'
import { EditableComponent } from '@adobe/aem-react-editable-components'
import {
  StoreDetails as UIFoundationsSD,
  LinkTargets,
  LinkFontSizes,
  Text,
  type LinkProps
} from '@dcxwam/dcx-wam-ui-foundations'

import type {
  GeoLocation,
  StoresSDKConfig,
  Store
} from '../../../services/stores/stores-sdk'
import { useIsSsr } from '../../../utils/isSsr'
import {
  createStoresSDK,
  getStore,
  isStore
} from '../../../services/stores/stores-sdk'
import { EditableComponentPropsType } from '../../../utils/prop-types'
import {
  StoreDetailsModelProps,
  OpenStatus,
  type StoreDetailsComponentProps,
  type StoreBreadCrumbConfig
} from './types'
import {
  getCategoryDetails,
  getCurrentDayOpenCloseMessage,
  getOpeningHours,
  getStoreServices,
  getPaymentMethods,
  transformStoreDetailsData,
  buildDataLayerStoreObj,
  getGoToMapLink,
  getCityLinkUrl,
  calculateStoreDistance,
  getFormattedAddress
} from './utils'
import { generateStoreLDJson } from './schema.utils'
import withComponentRefresh from '../../../utils/aem/editor/withComponentRefresh'
import { useExtractEditableProps } from '../../../utils/aem/editor/extractEditableProps'
import {
  withErrorBoundary,
  ErrorFallback
} from '../../functional/ErrorBoundary'
import { useUserLoginInfo } from '../../../utils/use-user-login-info'
import {
  getCoresiteInteractions,
  updateCoresiteInteractions
} from '../../../utils/local-storage/interactions'
import ExperienceFragmentContent from '../../templates/ExperienceFragmentContent/ExperienceFragmentContent'
import { PageConfig, type CbxConfigType } from '../../../utils/page-config'
import { pushDataLayer } from '../../../utils/datalayer'
import { setBreadcrumbTitle } from '../../../utils/breadcrumb-context'
import { useUserLocation } from '../Stores/common/hooks/useUserLocation'
import { getAkamaiHeaders } from '../../../utils/api-headers'

const fetchStoreDetails = async (config: StoresSDKConfig, storeId: string) => {
  const storesSDK = createStoresSDK(config)
  return getStore(storesSDK, storeId)
}

const StoreDetails: FC<EditableComponentPropsType<StoreDetailsModelProps>> =
  withComponentRefresh((props) => {
    const { isUserLoggedIn } = useUserLoginInfo()
    const positionOptions = useMemo(
      () => ({
        enableHighAccuracy: true,
        maximumAge: 300000,
        timeout: 10000
      }),
      []
    )
    const { userLocation } = useUserLocation(positionOptions)

    const {
      initialStoreData,
      storeLocatorConfig,
      storeDetailsConfig,
      storeImage,
      storeId,
      xfContent,
      xfContentPath,
      ...rest
    } = props.model

    const isSsr = useIsSsr()

    const [storeData, setStoreData] = useState<Store | undefined>(
      isStore(initialStoreData) ? initialStoreData : undefined
    )
    const { cbxConfig, lang, market } = PageConfig.getConfig() as {
      cbxConfig: CbxConfigType
      lang: string
      market: string
    }

    useEffect(() => {
      if (initialStoreData || !cbxConfig) {
        if (initialStoreData && 'name' in initialStoreData) {
          setBreadcrumbTitle(initialStoreData.name)
        }
        return
      }
      const fetchStoreData = async () => {
        const {
          cbxUrl: baseCbxUrl,
          market: cbxMarket,
          applicationId,
          loggerEnabled
        } = cbxConfig

        const data = await fetchStoreDetails(
          {
            baseCbxUrl,
            market: cbxMarket,
            lang,
            applicationId,
            loggerEnabled
          },
          storeId
        )

        if (isStore(data)) {
          setStoreData(data)
        }
      }
      fetchStoreData()
    }, [])
    const { labels, configs } = transformStoreDetailsData({
      storeData,
      storeLocatorConfig,
      storeDetailsConfig
    })
    const {
      closedLabel,
      openingHoursNotAvailable = '',
      openingHoursTitle,
      bookAnAppointmentLabel,
      openedUntilLabel = '',
      willOpenAtLabel = '',
      mapButtonLabel,
      showDirectionsLabel,
      distanceLabel,
      paymentMethodsTitle,
      servicesListTitle,
      breadcrumbCountryLabel,
      goToMapViewLabel
    } = labels
    const {
      storeLocation,
      storeAddress,
      googleMapsAPIKey,
      days,
      enable12HourFormat,
      enableMilesAsDistanceUnit,
      enableExpandedOpeningHoursView,
      bookAnAppointmentLink,
      loginPageLink,
      categories,
      mapZoomLevel,
      mapUserIcon,
      shouldShowBookAnAppointment,
      shouldShowMapService,
      paymentsConfig,
      servicesConfig,
      storeDirectoryPageUrl,
      storeDirectoryCities,
      storeMapPageUrl,
      storeAddressFormatTemplate
    } = configs

    const editableProps = useExtractEditableProps(props, rest)

    const category = getCategoryDetails(categories, storeData?.type ?? '')

    const currentDay = getCurrentDayOpenCloseMessage(
      storeData?.openingHours || [],
      {
        labels: {
          openedUntilLabel: openedUntilLabel ?? '',
          willOpenAtLabel: willOpenAtLabel ?? '',
          openingHoursNotAvailable: openingHoursNotAvailable ?? ''
        },
        days,
        enable12HourFormat
      }
    ) || {
      status: OpenStatus.Closed,
      message: ''
    }

    const appointmentLinkUrl = isUserLoggedIn
      ? bookAnAppointmentLink
      : `${
          loginPageLink
        }?referrer=${encodeURIComponent(bookAnAppointmentLink ?? '')}`

    const handleBookAnAppointment = useCallback(() => {
      pushDataLayer([
        {
          key: 'event',
          value: 'journey_event'
        },
        {
          key: 'journeyName',
          value: 'book appointment'
        },
        {
          key: 'stepName',
          value: 'start'
        }
      ])
      const coresiteInteractions = getCoresiteInteractions()
      updateCoresiteInteractions({
        booking: {
          ...coresiteInteractions?.booking,
          selectedStore: storeData
        }
      })
    }, [storeData])

    const getDistanceFromStore = useCallback(
      (userGeoLocation?: GeoLocation) => {
        return calculateStoreDistance(storeLocation, userGeoLocation, {
          enableMilesAsDistanceUnit,
          isSsr
        })
      },
      [storeLocation, enableMilesAsDistanceUnit, isSsr]
    )

    const openingHoursContent = getOpeningHours(
      (storeData?.openingHours ?? []) as Array<{
        openingTime: string | null
        closingTime: string | null
        isClosed?: boolean
        dayOfWeek: {
          index: number
          name: string
        }
      }>,
      {
        days,
        enable12HourFormat,
        enableExpandedOpeningHoursView,
        closedLabel
      }
    )

    const enabledServices = getStoreServices(
      servicesConfig,
      (storeData?.services ?? []) as string[]
    )

    const paymentMethods = getPaymentMethods(
      paymentsConfig,
      (storeData?.paymentMethods ?? []) as string[]
    )

    const linkPropsList: LinkProps[] = []
    if (breadcrumbCountryLabel) {
      linkPropsList.push({
        label: breadcrumbCountryLabel,
        fontSize: LinkFontSizes.Body1
      })
    }
    if (storeAddress?.city) {
      linkPropsList.push({
        children: (
          <Text content={`<strong>${storeAddress.city}</strong>`} isHtml />
        ),
        linkUrl: getCityLinkUrl(
          storeDirectoryPageUrl,
          storeAddress.city,
          storeDirectoryCities
        ),
        'aria-current': undefined
      })
    }

    const goToMapLink = getGoToMapLink(
      goToMapViewLabel,
      storeMapPageUrl,
      storeLocation
    )

    const storeBreadCrumbConfig: StoreBreadCrumbConfig | undefined =
      linkPropsList.length || goToMapLink
        ? {
            linkPropsList,
            ...(goToMapLink ? { goToMapLink } : {})
          }
        : undefined

    const storeLocationMapProps =
      shouldShowMapService && storeLocation
        ? {
            apiKey: googleMapsAPIKey ?? '',
            storeLocation: {
              latitude: Number(storeLocation.latitude),
              longitude: Number(storeLocation.longitude),
              name: `${storeData?.name ?? ''}`
            },
            zoom: mapZoomLevel,
            getDirectionsLabel: mapButtonLabel,
            storePinSvgUrl: category?.hoveredImage ?? category?.image,
            userLocationSvgUrl: mapUserIcon,
            language: lang,
            region: market?.toUpperCase(),
            userLocation
          }
        : undefined

    const mapConfig = storeLocationMapProps
      ? {
          apiKey: googleMapsAPIKey ?? '',
          userLocation,
          language: lang,
          region: market?.toUpperCase()
        }
      : undefined

    const storeServicesProps =
      enabledServices.length > 0
        ? {
            title: servicesListTitle,
            services: enabledServices
          }
        : undefined

    const openingHoursProps =
      openingHoursContent.length > 0
        ? {
            title: openingHoursTitle,
            openingHours: openingHoursContent
          }
        : undefined

    const paymentMethodsProps =
      paymentMethods.length > 0
        ? {
            title: paymentMethodsTitle,
            paymentMethods,
            imageConfig: {
              width: '72px',
              lazyLoading: true
            }
          }
        : undefined

    const storeDetailsContainerProps = {
      ...(storeBreadCrumbConfig ? { storeBreadCrumbConfig } : {}),
      name: storeData?.name,
      address: storeAddress
        ? {
            addressLine1: storeAddress.addressLine1 || '',
            postalCode: storeAddress.postalCode || '',
            town: storeAddress.city || '',
            countryCode: storeAddress.countryCode || '',
            formattedAddress: getFormattedAddress(
              storeAddress,
              storeAddressFormatTemplate
            ),
            location: storeAddress.location
              ? {
                  latitude: storeAddress.location.latitude || 0,
                  longitude: storeAddress.location.longitude || 0
                }
              : {
                  latitude: 0,
                  longitude: 0
                }
          }
        : undefined,
      categoryLabel: category?.ariaLabel ?? '',
      categoryIcon: category?.image ?? '',
      currentDay,
      phone: storeData?.phone,
      storeImage,
      ...(shouldShowBookAnAppointment && {
        bookAnAppointmentConfig: {
          label: bookAnAppointmentLabel,
          icon: 'pmi-icon-bookings',
          onClick: handleBookAnAppointment,
          linkUrl: appointmentLinkUrl
        }
      }),
      ...(storeServicesProps ? { storeServices: storeServicesProps } : {}),
      ...(openingHoursProps ? { openingHours: openingHoursProps } : {}),
      ...(paymentMethodsProps ? { paymentMethods: paymentMethodsProps } : {}),
      storeLocationDistanceLabel: distanceLabel,
      getDistanceFromStore,
      ...(showDirectionsLabel && {
        storeDirectionConfig: {
          label: showDirectionsLabel,
          onClick: () =>
            pushDataLayer(
              buildDataLayerStoreObj('StoreLocatorDirections', storeData)
            ),
          icon: 'pmi-icon-direction',
          linkTarget: LinkTargets.Blank
        }
      }),
      storePhoneNumberConfig: {
        onClick: () =>
          pushDataLayer(buildDataLayerStoreObj('StoreLocatorCall', storeData))
      },
      ...(mapConfig ? { mapConfig } : {}),
      ...(storeLocationMapProps
        ? { storeLocationMap: storeLocationMapProps }
        : {})
    } as StoreDetailsComponentProps
    const xfContentConfig =
      xfContentPath || xfContent
        ? {
            model: {
              xfPath: xfContentPath ?? '',
              xfContent: xfContent ?? undefined
            }
          }
        : undefined
    return (
      <EditableComponent {...editableProps}>
        <UIFoundationsSD {...storeDetailsContainerProps} />
        {xfContentConfig && <ExperienceFragmentContent {...xfContentConfig} />}
      </EditableComponent>
    )
  })

// @ts-expect-error Property 'getServerSideProps' does not exist
StoreDetails.getServerSideProps = async ({ storeId, ...props }) => {
  const { cbxConfig, lang } = PageConfig.getConfig() as {
    cbxConfig: CbxConfigType
    lang: string
  }
  const { cbxUrl: baseCbxUrl, market, applicationId, loggerEnabled } = cbxConfig

  const initialStoreData = await fetchStoreDetails(
    {
      baseCbxUrl,
      market,
      lang,
      applicationId,
      loggerEnabled,
      fresh: true,
      ...getAkamaiHeaders()
    },
    storeId
  )

  return {
    ...props,
    storeId,
    initialStoreData
  }
}

// @ts-expect-error Property 'getHeadData' does not exist
// eslint-disable-next-line @typescript-eslint/no-explicit-any
StoreDetails.getHeadData = async (props: Record<string, any>) => {
  const { initialStoreData, storeImage, pageUrl: storeUrl } = props ?? {}
  const {
    name: storeName,
    address,
    openingHours,
    phone
  } = initialStoreData ?? {}
  const formattedAddress = address?.formattedAddress ?? ''

  let pageTitle = storeName
  let description = storeName
  if (formattedAddress.trim()) {
    description = `${description}, ${formattedAddress}`
  }

  const jsonLD = generateStoreLDJson({
    storeName: storeName ?? '',
    storeUrl: storeUrl ?? '',
    telephone: phone ?? '',
    storeImage: storeImage?.image?.src,
    description,
    address,
    openingHours
  })

  const streetAddress = jsonLD?.address?.streetAddress ?? ''
  const storeSeoAddress = streetAddress || formattedAddress
  pageTitle = `${pageTitle} | ${storeSeoAddress}`

  return {
    status: initialStoreData?.errors?.[0]?.status ?? 200,
    seoTitle: pageTitle,
    seoDescription: description,
    assetFullUrl: storeImage?.assetFileReference,
    jsonLD,
    dataLayerPageName: storeName ?? '',
    dynamicPageTitle: storeName ?? ''
  }
}

const StoreDetailsWithErrorBoundary = withErrorBoundary(StoreDetails, {
  componentName: 'StoreDetails',
  fallback: <ErrorFallback componentName="StoreDetails" />
})

export default StoreDetailsWithErrorBoundary
